import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import StatusStepper from '../StatusStepper.jsx';
import { useClientData } from '../ClientLayout.jsx';
import { api, demoStore } from '../../lib/api.js';
import { reportAppNotification } from '../../lib/appNotifications.js';
import {
  duplicateInvoiceDemandMessage,
  findDemandWithInvoiceId,
  normalizeInvoiceId,
} from '../../lib/invoiceDemand.js';
import {
  appendLocalMessage,
  attachmentsFromMatter,
  deleteLocalMessage,
  getLocalMessages,
  mergeMessageLists,
  seedDemoMessages,
} from '../../lib/communicationsStore.js';
import {
  isMatterDetailPreviewId,
  seedMatterDetailPreview,
} from '../../lib/matterDetailPreviewSeed.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import {
  canEditMatterEvidence,
  canRemoveMatterEvidence,
  getPipelineIndex,
  isMatterRecordLocked,
  statusDisplayLabel,
} from '../../lib/matterPipeline.js';
import {
  attachAccountingToPayment,
  canRecordDebtorPayment,
  isPaidBeforeAssignment,
  isPaidStatus,
  listMatterPaymentEntries,
  matterBalanceRemaining,
  removeDebtorPayment,
  resolveDebtorPayment,
} from '../../lib/paidBeforeAssignment.js';
import MessageComposer, { MessageBubble } from '../MessageComposer.jsx';
import { messageComposerPlaceholder } from '../../lib/messageComposerExtras.js';
import { scrollMessageThreadToLatest } from '../../lib/messageThreadScroll.js';
import MatterKpiStrip from '../MatterKpiStrip.jsx';
import PaymentProofAttachments, {
  PaymentProofUploader,
} from '../PaymentProofAttachments.jsx';
import TrashIcon from '../TrashIcon.jsx';
import {
  buildLocalMessage,
  filterMessageAttachmentFiles,
  mergeMessageAttachmentNames,
  messageAttachmentUrl,
  messageHasContent,
} from '../../lib/messageAttachments.js';
import { deletePaymentProofFiles, putPaymentProofFiles } from '../../lib/paymentProofStore.js';
import {
  deleteUploadBlobs,
  putUploadBlobs,
} from '../../lib/uploadBlobStore.js';
import { AMOUNT_OWED_TIP, disputeLabel, disputeTooltip } from '../../lib/disputeTypes.js';
import DisputeTypeSelect from '../DisputeTypeSelect.jsx';
import FieldTip from '../FieldTip.jsx';
import EvidenceUpload from '../EvidenceUpload.jsx';
import {
  fileNamesFromEvidenceUrl,
  mergeEvidenceFileNames,
  pendingEvidenceUrl,
  resolveEvidenceVaultUrl,
} from '../../lib/evidenceFiles.js';
import {
  applyMatterPatchToRow,
  getMatterPatch,
  saveMatterPatch,
} from '../../lib/localMatterPatches.js';
import { useSaveToast } from '../SaveToast.jsx';
import {
  canUploadEvidence,
  estimateFileBytes,
  evidenceVaultLimitMessage,
  evidenceVaultWarnMessage,
} from '../../lib/evidenceVault.js';
import {
  demandLetterFileName,
  demandLetterViewerUrl,
} from '../../lib/demandLetter.js';
import { getLocalSettings, connectionsFromSettings } from '../../lib/settingsStore.js';
import { syncMattersToAccounting } from '../../lib/bulkDemandActions.js';
import {
  commitCurrencyAmount,
  formatCurrencyInputDisplay,
  isValidCurrencyAmount,
  parseCurrencyInputValue,
  sanitizeCurrencyInput,
} from '../../lib/currencyInput.js';
import {
  MATTER_CURRENCIES,
  currencyDisplaySymbol,
  formatMatterMoney,
  normalizeMatterCurrency,
} from '../../lib/matterCurrency.js';
import {
  isValidPostalForCountry,
  normalizePostalCode,
  postalCodeErrorMessage,
} from '../../lib/postalCode.js';
import {
  PAYMENT_PLAN_HINT,
  buildInstallmentPlanOptions,
  parseInstallmentSelection,
  selectionFromMatter,
} from '../../lib/installmentPlans.js';
import {
  SETTLEMENT_CHOICE_HINT,
  SETTLEMENT_CHOICES,
  normalizeSettlementChoice,
  settlementChoiceFromMatter,
  settlementChoiceLabel,
} from '../../lib/settlementChoice.js';
import {
  debtorDisplayName,
  debtorIdentityErrorMessage,
  normalizeDebtorIdentity,
} from '../../lib/debtorIdentity.js';
import { normalizeDebtorCountry } from '../../lib/debtorCountry.js';
import {
  HISTORY_EVENT_TYPES,
  appendLocalMatterHistory,
  createHistoryEvent,
  ensureCreatedHistory,
  getLocalMatterHistory,
  historyTypeLabel,
  mergeHistoryLists,
  setLocalMatterHistory,
  sortHistory,
  summarizeMatterChanges,
} from '../../lib/matterHistory.js';
import {
  buildAttorneyUpdateMessage,
  buildDebtorPaymentAttorneyMessage,
  hasNotifiableFieldChanges,
  shouldOfferAttorneyNotify,
} from '../../lib/attorneyNotify.js';
import { maybeAutoArchivePaidMatter } from '../../lib/autoArchivePaid.js';
import { archiveMatters, unarchiveMatters } from '../../lib/bulkDemandActions.js';
import { listMatterNoteEntries } from '../../lib/recordNotes.js';
import {
  fingerprintForeignEntries,
  resolveTabNotifications,
} from '../../lib/recordTabNotifications.js';
import {
  accountingPaymentDeleteFlashMessage,
  buildMatterStatusBanner,
  buildPaymentDeleteFlashMessage,
  buildPaymentFlashMessage,
} from '../../lib/matterStatusBanner.js';
import {
  debtorRecordKey,
  findDebtorRecord,
} from '../../lib/debtorRecords.js';
import RecordNotesSection from '../RecordNotesSection.jsx';
import { hasTrackingNumber, mailCarrierLabel, mailTrackingUrl } from '../../lib/uspsTracking.js';

const MATTER_TABS = [
  { id: 'details', label: 'Demand Details', shortLabel: 'Details' },
  { id: 'payments', label: 'Payments', shortLabel: 'Payments' },
  { id: 'messages', label: 'Messages', shortLabel: 'Messages' },
  { id: 'history', label: 'History', shortLabel: 'History' },
  { id: 'notes', label: 'Notes', shortLabel: 'Notes' },
];

function normalizeTab(raw) {
  const id = String(raw || '').trim().toLowerCase();
  return MATTER_TABS.some((tab) => tab.id === id) ? id : 'details';
}

function MoreActionsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="5" cy="12" r="1.7" fill="currentColor" />
      <circle cx="12" cy="12" r="1.7" fill="currentColor" />
      <circle cx="19" cy="12" r="1.7" fill="currentColor" />
    </svg>
  );
}

function isPreviewImage(name = '') {
  return /\.(png|jpe?g|gif|webp)$/i.test(String(name));
}

function PreviewCloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Fit a PDF to the column width and hide the viewer's extra pane and scrollbars. */
function previewFrameSrc(file) {
  const url = String(file?.url || '');
  if (!url) return '';
  const name = String(file?.name || '');
  const withoutHash = url.split('#')[0];
  const path = withoutHash.split('?')[0];
  const isPdf = /\.pdf$/i.test(name) || /\.pdf$/i.test(path);
  if (!isPdf) return url;
  const hash = url.includes('#') ? url.slice(url.indexOf('#') + 1) : '';
  const extras = [];
  if (!/(^|&)navpanes=/.test(hash)) extras.push('navpanes=0');
  if (!/(^|&)scrollbar=/.test(hash)) extras.push('scrollbar=0');
  if (!/(^|&)view=/.test(hash) && !/(^|&)zoom=/.test(hash)) extras.push('view=FitH');
  if (!extras.length) return url;
  return `${withoutHash}#${[hash, ...extras].filter(Boolean).join('&')}`;
}

function DemandFilePreview({ file, onClose }) {
  if (!file?.url) return null;
  const image = isPreviewImage(file.name);
  return (
    <aside className="matter-record-preview" aria-label={`Preview of ${file.name}`}>
      <div className="matter-record-preview-head">
        <strong title={file.name}>{file.name}</strong>
        <button
          type="button"
          className="matter-record-preview-close"
          onClick={onClose}
          aria-label="Close"
        >
          <PreviewCloseIcon />
        </button>
      </div>
      <div className="matter-record-preview-frame">
        {image ? (
          <img src={file.url} alt={file.name} />
        ) : (
          <iframe title={file.name} src={previewFrameSrc(file)} scrolling="no" />
        )}
      </div>
    </aside>
  );
}

function formatWhen(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formFromMatter(matter) {
  const evidenceUrl = matter?.Evidence_Vault_URL || '';
  const fromUrl = fileNamesFromEvidenceUrl(evidenceUrl);
  const fromMatter = Array.isArray(matter?.evidenceFileNames)
    ? matter.evidenceFileNames.map((n) => String(n || '').trim()).filter(Boolean)
    : [];
  // Prefer an explicit name list (local patch) when it has at least as many files.
  const evidenceFileNames =
    fromMatter.length >= fromUrl.length && fromMatter.length > 0 ? fromMatter : fromUrl;
  return {
    Debtor_Name: matter?.Debtor_Name || '',
    Debtor_Company: matter?.Debtor_Company || '',
    Debtor_Country: normalizeDebtorCountry(matter?.Debtor_Country, {
      postalCode: matter?.Debtor_Postal_Code,
      state: matter?.Debtor_State,
    }),
    Debtor_Street: matter?.Debtor_Street || '',
    Debtor_City: matter?.Debtor_City || '',
    Debtor_State: matter?.Debtor_State || '',
    Debtor_Postal_Code: matter?.Debtor_Postal_Code || '',
    Debtor_Email: matter?.Debtor_Email || '',
    Amount_Owed:
      matter?.Amount_Owed != null && Number.isFinite(Number(matter.Amount_Owed))
        ? Number(matter.Amount_Owed).toFixed(2)
        : '',
    Currency: normalizeMatterCurrency(matter?.Currency || matter?.currency),
    External_Invoice_ID: matter?.External_Invoice_ID || '',
    External_Account_Number: matter?.External_Account_Number || '',
    Due_Date: String(matter?.Due_Date || '').slice(0, 10),
    Tracking_Number: matter?.Tracking_Number || '',
    Dispute_Type: matter?.Dispute_Type || '',
    Dispute_Other: matter?.Dispute_Other || '',
    Evidence_Vault_URL: evidenceUrl,
    installmentSelection: selectionFromMatter(matter),
    settlementChoice: settlementChoiceFromMatter(matter),
    evidenceFileNames,
  };
}

/** Keep in-memory matter.Country aligned with the form default (USA when unset). */
function matterWithNormalizedCountry(matter) {
  if (!matter) return matter;
  return {
    ...matter,
    Debtor_Country: normalizeDebtorCountry(matter.Debtor_Country, {
      postalCode: matter.Debtor_Postal_Code,
      state: matter.Debtor_State,
    }),
  };
}

/** Build the comparable save payload from the current form (no validation). */
function matterDraftFromForm(form, record) {
  const plan = parseInstallmentSelection(form.installmentSelection);
  const settlementChoice = normalizeSettlementChoice(form.settlementChoice);
  const debtor = normalizeDebtorIdentity({
    contactName: form.Debtor_Name,
    company: form.Debtor_Company,
  });
  return {
    Debtor_Name: debtor.Debtor_Name,
    Debtor_Company: debtor.Debtor_Company,
    Debtor_Country: normalizeDebtorCountry(form.Debtor_Country, {
      postalCode: form.Debtor_Postal_Code,
      state: form.Debtor_State,
    }),
    Debtor_Street: form.Debtor_Street.trim(),
    Debtor_City: form.Debtor_City.trim(),
    Debtor_State: form.Debtor_State.trim().toUpperCase(),
    Debtor_Postal_Code: normalizePostalCode(form.Debtor_Postal_Code),
    Debtor_Email: form.Debtor_Email.trim(),
    Amount_Owed: Number(form.Amount_Owed),
    Currency: normalizeMatterCurrency(form.Currency),
    External_Invoice_ID: form.External_Invoice_ID.trim() || null,
    External_Account_Number: form.External_Account_Number.trim() || null,
    // Due date is attorney-owned; clients never write it.
    Due_Date: String(record?.Due_Date || '').trim().slice(0, 10) || null,
    Tracking_Number: record?.Tracking_Number || null,
    Dispute_Type: form.Dispute_Type || null,
    Dispute_Other: null,
    Evidence_Vault_URL: resolveEvidenceVaultUrl(
      record?.Evidence_Vault_URL,
      form.evidenceFileNames
    ),
    Installment_Plan: plan.installmentPlan,
    Payment_Structure: plan.paymentStructure,
    Payment_Term_ID: plan.paymentTermId,
    Settlement_Choice: settlementChoice,
  };
}

/** Apply a local offline patch (if any) onto a loaded matter row. */
function matterWithLocalPatch(matter) {
  return applyMatterPatchToRow(matter);
}

const READ_ONLY_FIELD_TIP = "This field can't be changed.";
const TRACKING_FIELD_TIP =
  'Only the attorney partner can set or update this after certified mail is sent.';
const ATTORNEY_FIELD_TIP =
  "Your attorney partner is assigned by DemandJet and can't be changed here.";
const DUE_DATE_FIELD_TIP =
  'Your attorney sets the payment deadline in the demand letter.';

function ReadOnlyControl({ tip = READ_ONLY_FIELD_TIP, children }) {
  return (
    <span className="field-readonly-control">
      {children}
      <span className="field-readonly-tip" role="tooltip">
        {tip}
      </span>
    </span>
  );
}

/** Plain text readout for always-locked or locked-record fields (not a disabled input). */
function FieldValue({ tip, children }) {
  const text =
    children == null || children === '' ? '—' : children;
  const value = <span className="field-value">{text}</span>;
  if (!tip) return value;
  return <ReadOnlyControl tip={tip}>{value}</ReadOnlyControl>;
}

function formatAmountDisplay(value, currency = 'USD') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return formatMatterMoney(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatOriginLabel(origin) {
  const normalized = String(origin || '').trim().toUpperCase();
  if (normalized === 'AUTOMATED') return 'Automated';
  if (normalized === 'MANUAL') return 'Manual';
  if (!normalized) return '—';
  return String(origin);
}

function formatShortWhen(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return String(value);
  }
}

export default function MatterDetailPage() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    allMatters,
    allDebtors,
    setMatters,
    vaultUsage,
    archivedMatters: archivedList,
    setNotice,
    openDebtor,
  } = useClientData();
  const { flashSave, clearSaveToast } = useSaveToast();
  const seedMatter = allMatters.find((m) => String(m.ROWID) === String(matterId)) || null;
  const seedId = seedMatter ? String(seedMatter.ROWID) : '';

  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(() => formFromMatter(seedMatter));
  const [matterAttachments, setMatterAttachments] = useState(() =>
    Array.isArray(seedMatter?.Attachments) ? seedMatter.Attachments : []
  );
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [notifyAttorney, setNotifyAttorney] = useState(true);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [pending, startTransition] = useTransition();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [notesRevision, setNotesRevision] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentProofFiles, setPaymentProofFiles] = useState([]);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [paymentError, setPaymentError] = useState('');
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState('');
  const actionsRef = useRef(null);
  const threadRef = useRef(null);
  const historyRef = useRef(null);
  const paymentFormRef = useRef(null);
  const activeTab = normalizeTab(searchParams.get('tab'));

  useEffect(() => {
    if (!filePreview) return undefined;
    function onKey(event) {
      if (event.key === 'Escape') setFilePreview(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filePreview]);

  function openEvidencePreview(file) {
    if (!file?.url) return;
    setFilePreview((current) =>
      current && current.name === file.name ? null : { name: file.name, url: file.url }
    );
  }

  function syncEvidenceViewerUrls(next) {
    setFilePreview((current) => {
      if (!current) return null;
      const url = next?.[current.name];
      if (!url || url === current.url) return current;
      return { name: current.name, url };
    });
  }

  const planOptions = useMemo(() => buildInstallmentPlanOptions(), []);

  const selectedPlan = planOptions.find((o) => o.value === form.installmentSelection);

  function rememberHistory(matterId, events) {
    const sorted = sortHistory(events || []);
    setLocalMatterHistory(matterId, sorted);
    setHistory(sorted);
    return sorted;
  }

  function logLocalHistory(matterId, partial) {
    const event = createHistoryEvent({
      matterId,
      ...partial,
      actorName: partial.actorName || user?.displayName || user?.email || '',
      actorRole: partial.actorRole || user?.role || 'CLIENT',
    });
    const next = appendLocalMatterHistory(matterId, event);
    setHistory(next);
    return next;
  }

  useEffect(() => {
    let cancelled = false;
    setError('');
    setDraft('');
    setNotifyAttorney(true);
    clearSaveToast();
    setLoadError('');
    setLoading(true);
    setDetail(null);
    setMessages([]);
    setHistory([]);
    setPaymentAmount('');
    setPaymentReference('');
    setPaymentProofFiles([]);
    setEvidenceFiles([]);
    setPaymentError('');
    setActionsOpen(false);

    (async () => {
      if (isMatterDetailPreviewId(matterId)) {
        const previewMatter =
          allMatters.find((m) => String(m.ROWID) === String(matterId)) || null;
        seedMatterDetailPreview(matterId, previewMatter);
      }
      try {
        const data = await api.get(`/server/client_api/matters/${encodeURIComponent(matterId)}`);
        if (cancelled) return;
        const matter = matterWithLocalPatch(matterWithNormalizedCountry(data.matter));
        setDetail({ ...data, matter });
        setForm(formFromMatter(matter));
        setMatterAttachments(
          Array.isArray(matter.Attachments) ? matter.Attachments : []
        );
        const remoteMessages = Array.isArray(data.messages) ? data.messages : [];
        const localMessages = getLocalMessages(matterId);
        const mergedMessages = mergeMessageLists(remoteMessages, localMessages);
        setMessages(mergedMessages);
        const remote = Array.isArray(data.history) ? data.history : [];
        const local = getLocalMatterHistory(matterId);
        const merged = mergeHistoryLists(remote, local);
        const seeded =
          merged.length > 0
            ? merged
            : ensureCreatedHistory(matter, {
                actorName: user?.displayName || user?.email || '',
                actorRole: user?.role || 'CLIENT',
              });
        rememberHistory(matterId, seeded);
        setLoading(false);
      } catch {
        if (cancelled) return;
        const seed = allMatters.find((m) => String(m.ROWID) === String(matterId));
        if (seed) {
          const demoMessages = demoStore.messages[seed.ROWID] || [];
          seedDemoMessages(seed.ROWID, demoMessages);
          if (isMatterDetailPreviewId(seed.ROWID)) {
            seedMatterDetailPreview(seed.ROWID, seed);
          }
          const local = getLocalMessages(seed.ROWID);
          const matter = matterWithLocalPatch(matterWithNormalizedCountry(seed));
          setDetail({
            matter,
            attorney: matter.Attorney_Name
              ? {
                  Display_Name: matter.Attorney_Name,
                  Email: matter.Attorney_Email || '',
                }
              : null,
            attachments: attachmentsFromMatter(matter),
            messages: local,
          });
          setForm(formFromMatter(matter));
          setMatterAttachments(
            Array.isArray(matter.Attachments) ? matter.Attachments : []
          );
          setMessages(local);
          const localHistory = getLocalMatterHistory(matter.ROWID);
          const seeded =
            localHistory.length > 0
              ? localHistory
              : ensureCreatedHistory(matter, {
                  actorName: user?.displayName || user?.email || '',
                  actorRole: user?.role || 'CLIENT',
                });
          rememberHistory(matter.ROWID, seeded);
          setLoading(false);
          return;
        }
        setLoadError('This demand record could not be found.');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matters read for seed fallback only
  }, [matterId, seedId]);

  useEffect(() => {
    if (activeTab !== 'messages') return;
    scrollMessageThreadToLatest(threadRef.current);
  }, [activeTab, messages]);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    function onPointerDown(e) {
      if (!actionsRef.current?.contains(e.target)) setActionsOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') setActionsOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [actionsOpen]);

  useEffect(() => {
    const el = document.getElementById(`matter-tab-${activeTab}`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [activeTab]);

  function setTab(nextId) {
    const id = normalizeTab(nextId);
    const next = new URLSearchParams(searchParams);
    if (id === 'details') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  }

  const record = detail?.matter || seedMatter;
  const fieldsLocked = isMatterRecordLocked(record?.Status);
  const showAssignedExtras = getPipelineIndex(record?.Status) >= 1;
  const canEditEvidence = canEditMatterEvidence(record?.Status);
  const canRemoveEvidence = canRemoveMatterEvidence(record?.Status);
  const isArchived = Boolean(record?.Archived);
  const evidenceAttachments = useMemo(() => {
    if (!record) return detail?.attachments || [];
    return attachmentsFromMatter({
      ...record,
      Attachments: matterAttachments,
    });
  }, [record, matterAttachments, detail?.attachments]);
  const pendingChangeSummary = useMemo(() => {
    if (!record || fieldsLocked) return { changes: [] };
    return summarizeMatterChanges(record, matterDraftFromForm(form, record));
  }, [record, form, fieldsLocked]);
  const offerAttorneyNotify =
    !fieldsLocked &&
    shouldOfferAttorneyNotify(record) &&
    hasNotifiableFieldChanges(pendingChangeSummary);

  const tabSignatures = useMemo(() => {
    const viewer = { email: user?.email, displayName: user?.displayName || user?.name };
    const paymentEvents = (history || []).filter((event) =>
      /paid|payment/i.test(String(event?.Summary || ''))
    );
    return {
      payments: fingerprintForeignEntries(paymentEvents, viewer),
      messages: fingerprintForeignEntries(messages, viewer),
      history: fingerprintForeignEntries(history, viewer),
      notes: fingerprintForeignEntries(listMatterNoteEntries(record?.ROWID), viewer),
    };
  }, [record?.ROWID, messages, history, notesRevision, user?.email, user?.displayName, user?.name]);

  const tabUnread = useMemo(() => {
    if (!record?.ROWID) return {};
    return resolveTabNotifications({
      kind: 'matter',
      recordId: record.ROWID,
      signatures: tabSignatures,
      activeTab,
    }).unread;
  }, [record?.ROWID, tabSignatures, activeTab]);

  function updateField(key, value) {
    if (fieldsLocked) return;
    setForm((prev) => ({ ...prev, [key]: value }));
    clearSaveToast();
  }

  function notifySaved(message) {
    const text = String(message || '').trim();
    if (!text) {
      clearSaveToast();
      return;
    }
    flashSave(text, {
      aboveMobileSave: !fieldsLocked && activeTab === 'details',
    });
  }

  function applyArchivedMatter(nextMatter) {
    if (!nextMatter?.ROWID) return;
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            matter: { ...(prev.matter || {}), ...nextMatter },
          }
        : prev
    );
    setMatters((prev) =>
      prev.map((m) =>
        String(m.ROWID) === String(nextMatter.ROWID) ? { ...m, ...nextMatter } : m
      )
    );
  }

  function handleArchiveToggle() {
    if (!record?.ROWID) return;
    setActionsOpen(false);
    if (isArchived) {
      const updated = unarchiveMatters([record]);
      const next = updated[0] || { ...record, Archived: false, Archived_At: null };
      applyArchivedMatter(next);
      logLocalHistory(record.ROWID, {
        type: HISTORY_EVENT_TYPES.RESTORED,
        summary: 'Demand restored from archive',
      });
      startTransition(async () => {
        try {
          await api.post(
            `/server/client_api/matters/${encodeURIComponent(record.ROWID)}/unarchive`,
            {}
          );
        } catch {
          // Local archive map already updated.
        }
      });
      setNotice?.('Demand restored to the active list.');
      navigate('/client/demands', { replace: true });
      return;
    }

    const result = archiveMatters([record], {
      currentArchivedCount: archivedList?.length || 0,
    });
    const next = result.updated[0];
    if (!next) {
      setNotice?.('Unable to archive this demand. Try again or contact support.');
      return;
    }
    applyArchivedMatter(next);
    logLocalHistory(record.ROWID, {
      type: HISTORY_EVENT_TYPES.ARCHIVED,
      summary: 'Demand archived',
    });
    startTransition(async () => {
      try {
        await api.post(
          `/server/client_api/matters/${encodeURIComponent(record.ROWID)}/archive`,
          {}
        );
      } catch {
        // Local archive map already updated.
      }
    });
    setNotice?.('Demand archived.');
    navigate('/client/demands/archived', { replace: true });
  }

  async function postAttorneyMessage(body, filesOrNames = []) {
    const text = String(body || '').trim();
    const files = Array.from(filesOrNames || []).filter((item) => item && typeof item === 'object' && item.name);
    const names = files.length
      ? files.map((file) => String(file.name || '').trim()).filter(Boolean)
      : (filesOrNames || []).map((n) => String(n || '').trim()).filter(Boolean);
    if (!record || !messageHasContent(text, names)) return false;
    const attachmentUrl = messageAttachmentUrl(names);

    async function rememberAttachments(message) {
      if (!files.length || !message?.ROWID) return;
      try {
        await putUploadBlobs('message', message.ROWID, files);
      } catch {
        // Local preview blobs are best-effort.
      }
    }

    try {
      const data = await api.post(`/server/client_api/matters/${record.ROWID}/messages`, {
        body: text,
        attachmentUrl,
      });
      await rememberAttachments(data.message);
      appendLocalMessage(record.ROWID, data.message);
      setMessages((prev) => [...prev, data.message]);
      try {
        const hist = await api.post(
          `/server/client_api/matters/${encodeURIComponent(record.ROWID)}/history`,
          {
            type: HISTORY_EVENT_TYPES.MESSAGE_SENT,
            summary: names.length
              ? 'Message with attachment sent to attorney'
              : 'Message sent to attorney',
          }
        );
        if (Array.isArray(hist.history)) {
          rememberHistory(
            record.ROWID,
            mergeHistoryLists(hist.history, getLocalMatterHistory(record.ROWID))
          );
        } else {
          logLocalHistory(record.ROWID, {
            type: HISTORY_EVENT_TYPES.MESSAGE_SENT,
            summary: names.length
              ? 'Message with attachment sent to attorney'
              : 'Message sent to attorney',
          });
        }
      } catch {
        logLocalHistory(record.ROWID, {
          type: HISTORY_EVENT_TYPES.MESSAGE_SENT,
          summary: names.length
            ? 'Message with attachment sent to attorney'
            : 'Message sent to attorney',
        });
      }
      return true;
    } catch {
      const message = buildLocalMessage({
        matterId: record.ROWID,
        senderName: user?.displayName || 'You',
        body: text,
        attachmentNames: names,
      });
      await rememberAttachments(message);
      const list = appendLocalMessage(record.ROWID, message);
      void reportAppNotification({
        kind: 'newMessage',
        matterId: record.ROWID,
        messageId: message.ROWID,
        snippet: text,
      });
      setMessages(list);
      logLocalHistory(record.ROWID, {
        type: HISTORY_EVENT_TYPES.MESSAGE_SENT,
        summary: names.length
          ? 'Message with attachment sent to attorney'
          : 'Message sent to attorney',
      });
      return true;
    }
  }

  function deleteAttorneyMessage(message) {
    if (!record?.ROWID || !message?.ROWID || deletingMessageId) return;
    setError('');
    setDeletingMessageId(message.ROWID);
    startTransition(() => {
      const removed = deleteLocalMessage(record.ROWID, message.ROWID);
      setDeletingMessageId('');
      if (!removed) {
        setError('Could not delete that message.');
        return;
      }
      void deleteUploadBlobs('message', message.ROWID);
      setMessages((prev) =>
        (prev || []).filter((item) => String(item?.ROWID) !== String(message.ROWID))
      );
      logLocalHistory(record.ROWID, {
        type: HISTORY_EVENT_TYPES.MESSAGE_DELETED,
        summary: 'Message deleted',
      });
      notifySaved('Message deleted.');
    });
  }

  async function maybeNotifyAttorneyOfSave(changeSummary) {
    if (!notifyAttorney || !shouldOfferAttorneyNotify(record)) return false;
    if (!hasNotifiableFieldChanges(changeSummary)) return false;
    // If the user already drafted a message, leave the thread alone.
    if (draft.trim() || attachmentFiles.length) return false;
    const body = buildAttorneyUpdateMessage(changeSummary);
    if (!body) return false;
    return postAttorneyMessage(body);
  }

  function onMessageAttachmentFilesChange(filesOrNames, { mode } = {}) {
    if (mode === 'remove') {
      const remove = new Set((filesOrNames || []).map((n) => String(n).toLowerCase()));
      setAttachmentFiles((prev) =>
        prev.filter((file) => !remove.has(String(file?.name || file || '').toLowerCase()))
      );
      return;
    }
    const billing = getLocalSettings(user?.email || '').billing || {};
    const accepted = filterMessageAttachmentFiles(filesOrNames);
    const addBytes = accepted.reduce(
      (sum, file) => sum + estimateFileBytes(file?.name || file, file?.size),
      0
    );
    const gate = canUploadEvidence({
      usedBytes: vaultUsage?.usedBytes || 0,
      addBytes,
      billing,
    });
    if (!gate.ok) {
      setError(evidenceVaultLimitMessage(gate.usage));
      return;
    }
    setError('');
    setAttachmentFiles((prev) => {
      const names = mergeMessageAttachmentNames(
        prev.map((file) => file.name),
        accepted
      );
      const byName = new Map(
        [...prev, ...accepted].map((file) => [String(file.name).toLowerCase(), file])
      );
      return names.map((name) => byName.get(String(name).toLowerCase())).filter(Boolean);
    });
  }

  function onEvidenceFiles(files) {
    if (!canEditEvidence) return;
    const billing = getLocalSettings(user?.email || '').billing || {};
    const accepted = Array.from(files || []).filter(Boolean);
    const addBytes = accepted.reduce(
      (sum, file) => sum + estimateFileBytes(file?.name, file?.size),
      0
    );
    const gate = canUploadEvidence({
      usedBytes: vaultUsage?.usedBytes || 0,
      addBytes,
      billing,
    });
    if (!gate.ok) {
      setError(evidenceVaultLimitMessage(gate.usage));
      return;
    }
    setEvidenceFiles((prev) => {
      const names = mergeEvidenceFileNames(
        prev.map((file) => file.name),
        accepted
      );
      const byName = new Map(
        [...prev, ...accepted].map((file) => [String(file.name).toLowerCase(), file])
      );
      return names.map((name) => byName.get(String(name).toLowerCase())).filter(Boolean);
    });
    if (record?.ROWID) {
      void putUploadBlobs('evidence', record.ROWID, accepted);
    }
    setForm((prev) => {
      const evidenceFileNames = mergeEvidenceFileNames(prev.evidenceFileNames, accepted);
      const Evidence_Vault_URL = pendingEvidenceUrl(evidenceFileNames) || '';
      // Draft-persist immediately so Save/API misses can't drop new files on refresh.
      if (record?.ROWID) {
        saveMatterPatch(record.ROWID, {
          Evidence_Vault_URL,
          evidenceFileNames,
          Attachments: matterAttachments,
          Updated_At: new Date().toISOString(),
        });
      }
      return {
        ...prev,
        evidenceFileNames,
        Evidence_Vault_URL,
      };
    });
  }

  function removeEvidenceFile(name) {
    if (!canRemoveEvidence) return;
    const target = String(name || '').toLowerCase();
    setFilePreview((current) =>
      current && String(current.name).toLowerCase() === target ? null : current
    );
    setEvidenceFiles((prev) =>
      prev.filter((file) => String(file?.name || '').toLowerCase() !== target)
    );
    if (record?.ROWID) {
      void deleteUploadBlobs('evidence', record.ROWID, [name]);
    }
    setForm((prev) => {
      const evidenceFileNames = prev.evidenceFileNames.filter(
        (item) => String(item || '').toLowerCase() !== target
      );
      const Evidence_Vault_URL = pendingEvidenceUrl(evidenceFileNames) || '';
      if (record?.ROWID) {
        const patch = {
          Evidence_Vault_URL,
          evidenceFileNames,
          Attachments: matterAttachments,
          Updated_At: new Date().toISOString(),
        };
        saveMatterPatch(record.ROWID, patch);
        setMatters((matters) =>
          matters.map((matter) =>
            String(matter.ROWID) === String(record.ROWID) ? { ...matter, ...patch } : matter
          )
        );
        void api
          .put(`/server/client_api/matters/${encodeURIComponent(record.ROWID)}`, {
            ...record,
            ...patch,
            lockerEvidenceDelete: true,
            historySummary: `Removed evidence file “${name}”`,
            historyType: 'EVIDENCE_UPDATED',
          })
          .catch(() => {});
      }
      return {
        ...prev,
        evidenceFileNames,
        Evidence_Vault_URL,
      };
    });
  }

  function removeMatterAttachment(file) {
    if (!canRemoveEvidence || !file) return;
    const fileName = String(file.name || file.fileName || file.filename || '').toLowerCase();
    setFilePreview((current) =>
      current && String(current.name).toLowerCase() === fileName ? null : current
    );
    setMatterAttachments((prev) => {
      const next = prev.filter((item) => {
        if (file.id && item.id) return String(item.id) !== String(file.id);
        const itemName = String(item.name || item.fileName || item.filename || '')
          .toLowerCase();
        const fileName = String(file.name || file.fileName || file.filename || '').toLowerCase();
        return itemName !== fileName;
      });
      if (record?.ROWID) {
        const patch = {
          Evidence_Vault_URL: form.Evidence_Vault_URL || record.Evidence_Vault_URL || '',
          evidenceFileNames: form.evidenceFileNames,
          Attachments: next,
          Updated_At: new Date().toISOString(),
        };
        saveMatterPatch(record.ROWID, patch);
        setMatters((matters) =>
          matters.map((matter) =>
            String(matter.ROWID) === String(record.ROWID) ? { ...matter, ...patch } : matter
          )
        );
        void api
          .put(`/server/client_api/matters/${encodeURIComponent(record.ROWID)}`, {
            ...record,
            ...patch,
            lockerEvidenceDelete: true,
            historySummary: `Removed evidence file “${file.name || file.fileName || 'attachment'}”`,
            historyType: 'EVIDENCE_UPDATED',
          })
          .catch(() => {});
      }
      return next;
    });
  }

  function saveRecord(e) {
    e.preventDefault();
    if (!record || fieldsLocked) return;
    setError('');
    clearSaveToast();

    // Union form names with any draft-persisted names so a stale render can't
    // overwrite files the user already added (and stored in localStorage).
    const draftPatch = getMatterPatch(record.ROWID);
    const evidenceFileNames = mergeEvidenceFileNames(
      Array.isArray(draftPatch?.evidenceFileNames)
        ? draftPatch.evidenceFileNames
        : form.evidenceFileNames || [],
      (form.evidenceFileNames || []).map((name) => ({ name }))
    );
    const formForSave = { ...form, evidenceFileNames };
    const payload = matterDraftFromForm(formForSave, record);
    const debtor = normalizeDebtorIdentity({
      contactName: form.Debtor_Name,
      company: form.Debtor_Company,
    });

    if (!debtor.ok || !payload.Debtor_State || !(payload.Amount_Owed > 0)) {
      setError(
        !debtor.ok
          ? debtorIdentityErrorMessage()
          : 'State and a positive amount owed are required.'
      );
      return;
    }
    if (!isValidCurrencyAmount(form.Amount_Owed)) {
      setError('Enter a valid amount owed with up to 2 decimal places.');
      return;
    }
    if (!isValidPostalForCountry(payload.Debtor_Postal_Code, payload.Debtor_Country)) {
      setError(postalCodeErrorMessage(payload.Debtor_Postal_Code, payload.Debtor_Country));
      return;
    }
    if (!payload.Dispute_Type) {
      setError('Please select a dispute type.');
      return;
    }
    const nextInvoiceId = form.External_Invoice_ID;
    if (
      normalizeInvoiceId(nextInvoiceId) !==
      normalizeInvoiceId(record.External_Invoice_ID)
    ) {
      const duplicateInvoice = findDemandWithInvoiceId(
        allMatters,
        nextInvoiceId,
        record.ROWID
      );
      if (duplicateInvoice) {
        setError(duplicateInvoiceDemandMessage(nextInvoiceId, duplicateInvoice));
        return;
      }
    }

    startTransition(async () => {
      const changeSummary = summarizeMatterChanges(record, payload);
      try {
        const data = await api.put(
          `/server/client_api/matters/${encodeURIComponent(record.ROWID)}`,
          {
            ...payload,
            historySummary: changeSummary.summary,
            historyType: changeSummary.type,
          }
        );
        const apiMatter = data.matter || {};
        const nextMatter = {
          ...record,
          ...apiMatter,
          ...payload,
          ROWID: record.ROWID,
          Status: apiMatter.Status || record.Status,
          Tracking_Number:
            apiMatter.Tracking_Number != null
              ? apiMatter.Tracking_Number
              : record.Tracking_Number,
          Created_At: apiMatter.Created_At || record.Created_At,
          Updated_At: apiMatter.Updated_At || new Date().toISOString(),
          Attorney_Name: apiMatter.Attorney_Name ?? record.Attorney_Name,
          Attorney_Email: apiMatter.Attorney_Email ?? record.Attorney_Email,
          Attorney_ID: apiMatter.Attorney_ID ?? record.Attorney_ID,
          Attachments: matterAttachments,
          evidenceFileNames,
        };
        const savedMatter = nextMatter;
        // Always keep a local evidence backup across refresh. Do not clear on API
        // success — preview/API rows have returned stale Evidence_Vault_URL before.
        saveMatterPatch(savedMatter.ROWID, savedMatter);
        setDetail((prev) => ({
          ...(prev || {}),
          matter: savedMatter,
          attorney: data.attorney || prev?.attorney || null,
          attachments: attachmentsFromMatter(savedMatter),
          history: data.history || prev?.history || [],
        }));
        setForm(formFromMatter(savedMatter));
        setMatterAttachments(
          Array.isArray(savedMatter.Attachments) ? savedMatter.Attachments : []
        );
        setMatters((prev) =>
          prev.map((m) =>
            String(m.ROWID) === String(savedMatter.ROWID) ? { ...m, ...savedMatter } : m
          )
        );
        if (Array.isArray(data.history) && data.history.length) {
          rememberHistory(
            savedMatter.ROWID,
            mergeHistoryLists(data.history, getLocalMatterHistory(savedMatter.ROWID))
          );
        } else {
          logLocalHistory(savedMatter.ROWID, {
            type: changeSummary.type,
            summary: changeSummary.summary,
          });
        }
        const notified = await maybeNotifyAttorneyOfSave(changeSummary);
        notifySaved(
          notified
            ? 'Demand record saved. Attorney notified of these changes.'
            : 'Demand record saved.'
        );
      } catch {
        const nextMatter = {
          ...record,
          ...payload,
          Attachments: matterAttachments,
          Updated_At: new Date().toISOString(),
          evidenceFileNames,
        };
        // Offline / missing PUT / stale API — keep evidence + field edits across refresh.
        saveMatterPatch(nextMatter.ROWID, nextMatter);
        setDetail((prev) => ({
          ...(prev || {}),
          matter: nextMatter,
          attachments: attachmentsFromMatter(nextMatter),
        }));
        setForm(formFromMatter(nextMatter));
        setMatterAttachments(
          Array.isArray(nextMatter.Attachments) ? nextMatter.Attachments : []
        );
        setMatters((prev) =>
          prev.map((m) => (String(m.ROWID) === String(nextMatter.ROWID) ? { ...m, ...nextMatter } : m))
        );
        logLocalHistory(nextMatter.ROWID, {
          type: changeSummary.type,
          summary: changeSummary.summary,
        });
        const notified = await maybeNotifyAttorneyOfSave(changeSummary);
        notifySaved(
          notified
            ? 'Demand record saved locally (API offline). Attorney notified of these changes.'
            : 'Demand record saved locally (API offline).'
        );
      }
    });
  }

  function applyPaymentResolution(resolution, historyEvents) {
    const nextStatus = resolution.fullyPaid
      ? resolution.status
      : resolution.patch?.Status || record.Status;
    const nextMatter = {
      ...record,
      ...resolution.patch,
      Status: nextStatus,
    };
    saveMatterPatch(nextMatter.ROWID, nextMatter);
    setDetail((prev) => ({
      ...(prev || {}),
      matter: nextMatter,
      attachments: attachmentsFromMatter(nextMatter),
      history: historyEvents || prev?.history || [],
    }));
    setForm(formFromMatter(nextMatter));
    setMatters((prev) =>
      prev.map((m) => (String(m.ROWID) === String(nextMatter.ROWID) ? { ...m, ...nextMatter } : m))
    );
    if (Array.isArray(historyEvents) && historyEvents.length) {
      rememberHistory(nextMatter.ROWID, historyEvents);
    } else {
      const paidLabel = Number(resolution.amountPaid);
      const partialSummary =
        Number.isFinite(paidLabel) && paidLabel > 0
          ? `Partial payment of $${paidLabel.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} recorded`
          : resolution.message || 'Partial payment recorded';
      logLocalHistory(nextMatter.ROWID, {
        type: resolution.fullyPaid
          ? HISTORY_EVENT_TYPES.STATUS_CHANGED
          : HISTORY_EVENT_TYPES.RECORD_UPDATED,
        summary: resolution.fullyPaid
          ? resolution.waived
            ? 'Resolved — paid before assignment (no per-demand fee)'
            : `Status changed to ${resolution.status}`
          : partialSummary,
      });
    }
  }

  function focusPaymentForm() {
    if (!record || !canRecordDebtorPayment(record)) return;
    const remaining = matterBalanceRemaining(record);
    if (!paymentAmount) {
      setPaymentAmount(remaining > 0 ? String(remaining) : '');
    }
    setPaymentError('');
    setTab('payments');
    setActionsOpen(false);
    requestAnimationFrame(() => {
      paymentFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      paymentFormRef.current?.querySelector('input[name="payment-amount"]')?.focus();
    });
  }

  function resetPaymentForm(nextRemaining) {
    setPaymentAmount(nextRemaining > 0 ? String(nextRemaining) : '');
    setPaymentReference('');
    setPaymentProofFiles([]);
    setPaymentError('');
  }

  function onPaymentProofFilesChange(filesOrNames, { mode } = {}) {
    if (mode === 'remove') {
      const remove = new Set((filesOrNames || []).map((n) => String(n).toLowerCase()));
      setPaymentProofFiles((prev) =>
        prev.filter((file) => !remove.has(String(file?.name || '').toLowerCase()))
      );
      return;
    }
    const accepted = filterMessageAttachmentFiles(filesOrNames);
    if (!accepted.length) return;
    setPaymentProofFiles((prev) => {
      const names = mergeMessageAttachmentNames(
        prev.map((file) => file.name),
        accepted
      );
      const byName = new Map(
        [...prev, ...accepted].map((file) => [String(file.name).toLowerCase(), file])
      );
      return names.map((name) => byName.get(String(name).toLowerCase())).filter(Boolean);
    });
    setPaymentError('');
  }

  function recordDebtorPayment(event) {
    event?.preventDefault?.();
    if (!record || !canRecordDebtorPayment(record)) return;
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError('Enter a payment amount greater than zero.');
      return;
    }
    const remaining = matterBalanceRemaining(record);
    if (amount > remaining) {
      setPaymentError(
        `Payment cannot exceed the remaining balance of ${formatAmountDisplay(
          remaining,
          form.Currency || record?.Currency
        )}.`
      );
      return;
    }
    const proofFiles = [...(paymentProofFiles || [])];
    const proofNames = proofFiles
      .map((file) => String(file?.name || '').trim())
      .filter(Boolean);
    const reference = String(paymentReference || '').trim();
    const paymentId = `pay_${Date.now()}`;

    setError('');
    clearSaveToast();
    setPaymentError('');
    startTransition(async () => {
      let resolution;
      let accounting = null;
      const localResolution = resolveDebtorPayment(record, {
        source: 'client_record_payment',
        amount,
        reference,
        paymentId,
        attachments: proofNames,
      });
      try {
        const data = await api.post(
          `/server/client_api/matters/${encodeURIComponent(record.ROWID)}/record-payment`,
          {
            source: 'client_record_payment',
            amount,
            reference,
            paymentId,
            attachments: proofNames,
          }
        );
        const serverMatter = data.matter || {};
        const serverEntries = listMatterPaymentEntries(serverMatter);
        const localEntries = listMatterPaymentEntries(localResolution.patch);
        // Prefer the client ledger when this payment is still partial — a stale
        // server Amount_Paid can falsely look like a full pay / fee waiver.
        const trustLocalPartial = localResolution.fullyPaid === false;
        resolution = {
          patch: {
            ...localResolution.patch,
            ...serverMatter,
            Amount_Paid: trustLocalPartial
              ? localResolution.patch.Amount_Paid
              : serverMatter.Amount_Paid != null
                ? serverMatter.Amount_Paid
                : localResolution.patch.Amount_Paid,
            Payment_Entries: trustLocalPartial
              ? localEntries
              : serverEntries.length
                ? serverEntries
                : localEntries,
            Paid_At: trustLocalPartial
              ? localResolution.patch.Paid_At
              : serverMatter.Paid_At || localResolution.patch.Paid_At,
            Paid_Source:
              serverMatter.Paid_Source || localResolution.patch.Paid_Source,
            Status: trustLocalPartial
              ? record.Status
              : serverMatter.Status || localResolution.patch.Status || record.Status,
          },
          waived: trustLocalPartial
            ? false
            : Boolean(data.waived ?? localResolution.waived),
          status: trustLocalPartial
            ? record.Status
            : data.status ||
              serverMatter.Status ||
              localResolution.status ||
              record.Status,
          fullyPaid: trustLocalPartial
            ? false
            : Boolean(
                data.fullyPaid ||
                  localResolution.fullyPaid ||
                  isPaidStatus(data.status || serverMatter.Status)
              ),
          amountPaid: data.amountPaid ?? localResolution.amountPaid ?? amount,
          balanceRemaining: trustLocalPartial
            ? localResolution.balanceRemaining
            : data.balanceRemaining ?? localResolution.balanceRemaining,
          message: trustLocalPartial
            ? localResolution.message
            : data.message ||
              localResolution.message ||
              (data.waived
                ? 'Payment recorded. Demand closed as paid before assignment — no per-demand fee.'
                : 'Payment recorded.'),
          payment: data.payment || localResolution.payment,
        };
        accounting = data.accounting || null;
        if (accounting?.synced && accounting.paymentId && (resolution.payment?.id || paymentId)) {
          const localPayId = resolution.payment?.id || paymentId;
          const stampedEntries = attachAccountingToPayment(
            listMatterPaymentEntries(resolution.patch),
            localPayId,
            accounting
          );
          resolution = {
            ...resolution,
            patch: {
              ...resolution.patch,
              Payment_Entries: stampedEntries,
            },
            payment: {
              ...(resolution.payment || {}),
              id: localPayId,
              accountingPaymentId: accounting.paymentId,
              accountingProvider: accounting.provider || null,
            },
          };
        }
        if (proofFiles.length) {
          const storeId =
            listMatterPaymentEntries(resolution.patch).slice(-1)[0]?.id || paymentId;
          try {
            await putPaymentProofFiles(record.ROWID, storeId, proofFiles);
          } catch {
            // Names still show on the entry; viewing needs a successful store.
          }
        }
        // Ignore server history when it may have closed the demand incorrectly.
        applyPaymentResolution(resolution, trustLocalPartial ? null : data.history);
      } catch (err) {
        try {
          resolution = localResolution;
          if (proofFiles.length) {
            const storeId =
              listMatterPaymentEntries(resolution.patch).slice(-1)[0]?.id || paymentId;
            try {
              await putPaymentProofFiles(record.ROWID, storeId, proofFiles);
            } catch {
              // Names still show on the entry; viewing needs a successful store.
            }
          }
          accounting = maybeLocalAccountingPaymentSync(record, resolution, user?.email);
          if (accounting?.synced) {
            const localPayId = resolution.payment?.id || paymentId;
            const stampedEntries = attachAccountingToPayment(
              listMatterPaymentEntries(resolution.patch),
              localPayId,
              accounting
            );
            resolution = {
              ...resolution,
              patch: {
                ...resolution.patch,
                Payment_Entries: stampedEntries,
              },
              payment: {
                ...(resolution.payment || {}),
                id: localPayId,
                accountingPaymentId: accounting.paymentId,
                accountingProvider: accounting.provider || null,
              },
            };
          }
          applyPaymentResolution(resolution);
        } catch (localErr) {
          setPaymentError(localErr?.message || err?.message || 'Could not record payment.');
          return;
        }
      }

      resetPaymentForm(resolution.balanceRemaining);

      const notifyBody = buildDebtorPaymentAttorneyMessage(resolution);
      const notified = await postAttorneyMessage(notifyBody);

      const settings = getLocalSettings(user?.email || '');
      const paidMatter = {
        ...record,
        ...resolution.patch,
        Status: resolution.fullyPaid ? resolution.status : record.Status,
      };
      const autoArchive =
        resolution.fullyPaid
          ? maybeAutoArchivePaidMatter(paidMatter, {
              automations: settings.automations || {},
              billing: settings.billing || {},
              currentArchivedCount: archivedList?.length || 0,
            })
          : { archived: false };
      if (autoArchive.archived) {
        setMatters((prev) =>
          prev.map((m) =>
            String(m.ROWID) === String(autoArchive.matter.ROWID)
              ? { ...m, ...autoArchive.matter }
              : m
          )
        );
        setDetail((prev) =>
          prev
            ? {
                ...prev,
                matter: { ...(prev.matter || {}), ...autoArchive.matter },
              }
            : prev
        );
        try {
          await api.post(
            `/server/client_api/matters/${encodeURIComponent(autoArchive.matter.ROWID)}/archive`,
            {}
          );
        } catch {
          // Local archive map already updated.
        }
      }

      // Always toast the Record Payment action (banner still owns status copy).
      const flash = buildPaymentFlashMessage(resolution, { notified, accounting });
      notifySaved(flash || 'Payment recorded.');
    });
  }

  function maybeLocalAccountingPaymentSync(matter, resolution, email) {
    const invoiceId = matter?.External_Invoice_ID || matter?.Invoice_ID;
    if (!invoiceId || !(Number(resolution?.amountPaid) > 0)) return null;
    const settings = getLocalSettings(email || '');
    const connections = connectionsFromSettings(settings);
    const connected = (connections || []).find((c) => c?.status === 'connected');
    if (!connected) return null;
    const paidMatter = {
      ...matter,
      ...resolution.patch,
      Status: resolution.fullyPaid ? resolution.status : matter.Status,
    };
    syncMattersToAccounting([paidMatter]);
    return {
      synced: true,
      mock: true,
      provider: connected.id || connected.provider || 'QUICKBOOKS',
      paymentId: `pay_demo_${Date.now()}`,
    };
  }

  function applyPaymentDeletion(resolution, historyEvents) {
    const nextStatus = resolution.reopened
      ? resolution.status
      : resolution.patch?.Status || record.Status;
    const nextMatter = {
      ...record,
      ...resolution.patch,
      Status: nextStatus,
    };
    saveMatterPatch(nextMatter.ROWID, nextMatter);
    setDetail((prev) => ({
      ...(prev || {}),
      matter: nextMatter,
      attachments: attachmentsFromMatter(nextMatter),
      history: historyEvents || prev?.history || [],
    }));
    setForm(formFromMatter(nextMatter));
    setMatters((prev) =>
      prev.map((m) => (String(m.ROWID) === String(nextMatter.ROWID) ? { ...m, ...nextMatter } : m))
    );
    if (Array.isArray(historyEvents) && historyEvents.length) {
      rememberHistory(nextMatter.ROWID, historyEvents);
    } else {
      logLocalHistory(nextMatter.ROWID, {
        type: resolution.reopened ? 'STATUS_CHANGED' : 'PAYMENT_DELETED',
        summary: resolution.message || 'Payment deleted',
      });
    }
    const remaining = matterBalanceRemaining(nextMatter);
    resetPaymentForm(remaining);
  }

  function deletePaymentEntry(entry) {
    if (!record || !entry?.id || pending) return;
    setError('');
    clearSaveToast();
    setPaymentError('');
    startTransition(async () => {
      let resolution;
      let accounting = null;
      const localResolution = removeDebtorPayment(record, entry.id);
      try {
        const data = await api.post(
          `/server/client_api/matters/${encodeURIComponent(record.ROWID)}/delete-payment`,
          { paymentId: entry.id }
        );
        const serverMatter = data.matter || {};
        resolution = {
          ...localResolution,
          patch: {
            ...localResolution.patch,
            ...serverMatter,
            Amount_Paid:
              serverMatter.Amount_Paid != null
                ? serverMatter.Amount_Paid
                : localResolution.patch.Amount_Paid,
            Payment_Entries: listMatterPaymentEntries(
              serverMatter.Payment_Entries != null ? serverMatter : localResolution.patch
            ),
            Status: data.reopened
              ? data.status || localResolution.status
              : serverMatter.Status || localResolution.patch.Status || record.Status,
            Paid_At:
              data.reopened
                ? null
                : serverMatter.Paid_At !== undefined
                  ? serverMatter.Paid_At
                  : localResolution.patch.Paid_At,
          },
          reopened: Boolean(data.reopened ?? localResolution.reopened),
          status: data.status || localResolution.status,
          message: data.message || localResolution.message,
          amountRemoved: data.amountRemoved ?? localResolution.amountRemoved,
          amountPaid: data.amountPaid ?? localResolution.amountPaid,
          balanceRemaining:
            data.balanceRemaining ?? localResolution.balanceRemaining,
          removed: data.removed || localResolution.removed,
        };
        accounting = data.accounting || null;
        applyPaymentDeletion(resolution, data.history);
        try {
          await deletePaymentProofFiles(
            record.ROWID,
            entry.id,
            resolution.removed?.attachments || entry.attachments || []
          );
        } catch {
          // Ledger delete already succeeded.
        }
      } catch (err) {
        try {
          resolution = localResolution;
          accounting = maybeLocalAccountingPaymentDelete(
            record,
            localResolution.removed,
            user?.email
          );
          applyPaymentDeletion(resolution);
          try {
            await deletePaymentProofFiles(
              record.ROWID,
              entry.id,
              resolution.removed?.attachments || entry.attachments || []
            );
          } catch {
            // Ledger delete already succeeded.
          }
        } catch (localErr) {
          setPaymentError(localErr?.message || err?.message || 'Could not delete payment.');
          return;
        }
      }

      const flash = buildPaymentDeleteFlashMessage(resolution, { accounting });
      notifySaved(flash || 'Payment deleted.');
    });
  }

  function maybeLocalAccountingPaymentDelete(matter, removedEntry, email) {
    const accountingPaymentId = removedEntry?.accountingPaymentId;
    if (!accountingPaymentId) return null;
    const settings = getLocalSettings(email || '');
    const connections = connectionsFromSettings(settings);
    const connected = (connections || []).find((c) => c?.status === 'connected');
    if (!connected) return null;
    // Local/demo sync map only tracks matter-level sync — deleting still reports success.
    syncMattersToAccounting([
      {
        ...matter,
        Status: isPaidStatus(matter.Status) ? matter.Status : matter.Status,
      },
    ]);
    return {
      deleted: true,
      mock: true,
      provider:
        removedEntry.accountingProvider || connected.id || connected.provider || 'QUICKBOOKS',
      paymentId: accountingPaymentId,
    };
  }

  function sendMessage(e) {
    e.preventDefault();
    if (!record) return;
    const body = draft.trim();
    if (!messageHasContent(body, attachmentFiles.map((file) => file.name))) return;
    setError('');

    startTransition(async () => {
      const ok = await postAttorneyMessage(body, attachmentFiles);
      if (ok) {
        setDraft('');
        setAttachmentFiles([]);
      }
    });
  }

  if (loading) {
    return (
      <div className="client-page matter-detail-page">
        <p className="settings-lead">Loading demand record…</p>
      </div>
    );
  }

  if (!record || loadError) {
    return (
      <div className="client-page matter-detail-page">
        <div className="section-head">
          <div>
            <h1>Demand not found</h1>
            <p>{loadError || 'This demand record is unavailable.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const attachments = evidenceAttachments;
  const attorney = detail?.attorney;
  const letterViewerUrl = demandLetterViewerUrl(record);
  const letterFileName = demandLetterFileName(record);
  const statusLabel = statusDisplayLabel(record.Status);
  const canRecordPayment = canRecordDebtorPayment(record);
  const paidBeforeAssignment = isPaidBeforeAssignment(record.Status);
  const paymentEntries = listMatterPaymentEntries(record);
  const balanceRemaining = matterBalanceRemaining(record);
  const demandId = String(record.Demand_ID || '').trim();
  const kpiMatter = {
    ...record,
    ...form,
    Amount_Owed: form.Amount_Owed || record.Amount_Owed,
    Amount_Paid: record.Amount_Paid,
    Payment_Entries: record.Payment_Entries,
    Status: record.Status,
    Created_At: record.Created_At,
    Paid_At: record.Paid_At,
    Payment_Term_ID: record.Payment_Term_ID || form.Payment_Term_ID,
    ROWID: record.ROWID,
  };
  const localSettings = getLocalSettings(user?.email || '');
  const paymentTerms = localSettings.paymentTerms || [];
  const originLabel = formatOriginLabel(record.Origin);
  const updatedLabel = formatShortWhen(record.Updated_At);
  const recordMetaParts = [statusLabel, originLabel];
  if (updatedLabel) recordMetaParts.push(`Updated ${updatedLabel}`);
  const recordMetaLine = recordMetaParts.filter(Boolean).join(' · ');

  function renderNotifyAttorneyCheckbox() {
    if (!offerAttorneyNotify) return null;
    return (
      <label className="checkbox matter-notify-attorney">
        <input
          type="checkbox"
          checked={notifyAttorney}
          onChange={(e) => setNotifyAttorney(e.target.checked)}
        />
        <span>Notify attorney of these changes</span>
      </label>
    );
  }

  const trackingHints = {
    country: form.Debtor_Country,
    postalCode: form.Debtor_Postal_Code,
    state: form.Debtor_State,
  };
  const canTrackPackage = hasTrackingNumber(form.Tracking_Number);
  const trackPackageLabel = canTrackPackage
    ? mailCarrierLabel(form.Tracking_Number, trackingHints)
    : 'Track';
  /** Track only when a tracking number exists and the demand is not paid. */
  const showTopTrackButton = canTrackPackage && !isPaidStatus(record?.Status);
  const isPaymentBeforeTrackStatus = [
    'CERTIFIED_MAIL_DISPATCHED',
    'DEMAND_DELIVERED',
    'LITIGATION_READY',
    'UNPAID_ESCALATE',
  ].includes(
    String(record?.Status || '')
      .trim()
      .toUpperCase()
  );
  function renderTrackPackageButton(className = 'btn-primary') {
    if (!showTopTrackButton) return null;
    return (
      <a
        className={className}
        href={mailTrackingUrl(form.Tracking_Number, trackingHints)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {trackPackageLabel}
      </a>
    );
  }

  function renderRecordPaymentButton() {
    if (!canRecordPayment) return null;
    return (
      <button
        type="button"
        className="btn-ghost"
        disabled={pending}
        onClick={focusPaymentForm}
      >
        Record Payment
      </button>
    );
  }

  const showSave = !fieldsLocked && activeTab === 'details';
  const previewOpen = Boolean(filePreview?.url) && activeTab === 'details';
  const pageClass = [
    'client-page matter-detail-page',
    showSave ? 'has-mobile-save' : '',
    previewOpen ? 'is-file-preview' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const statusBanner = buildMatterStatusBanner(record);
  const linkedDebtorId = debtorRecordKey(form);
  const linkedDebtor =
    findDebtorRecord(allDebtors || [], linkedDebtorId) ||
    (linkedDebtorId && linkedDebtorId !== '||'
      ? {
          id: linkedDebtorId,
          name: debtorDisplayName(form) || 'Debtor',
          company: form.Debtor_Company || '',
        }
      : null);
  const contactAddress = [
    form.Debtor_Street,
    [form.Debtor_City, form.Debtor_State, form.Debtor_Postal_Code].filter(Boolean).join(', '),
    form.Debtor_Country,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' · ');

  return (
    <div className={pageClass}>
      <div className="section-head matter-detail-page-head">
        <div>
          <h1>{debtorDisplayName(form) || debtorDisplayName(record)}</h1>
          {demandId ? (
            <p className="matter-demand-id-subtitle">
              <strong>Demand ID</strong>: {demandId}
            </p>
          ) : null}
        </div>
        <div className="matter-detail-page-actions debtor-detail-actions">
          <div className="debtor-detail-actions-desktop">
            <button type="button" className="btn-ghost" onClick={handleArchiveToggle}>
              {isArchived ? 'Unarchive' : 'Archive'}
            </button>
            {isPaymentBeforeTrackStatus ? (
              <>
                {renderRecordPaymentButton()}
                {showTopTrackButton
                  ? renderTrackPackageButton('btn-primary matter-detail-track-btn')
                  : null}
              </>
            ) : (
              <>
                {showTopTrackButton
                  ? renderTrackPackageButton('btn-primary matter-detail-track-btn')
                  : null}
                {renderRecordPaymentButton()}
              </>
            )}
            {showSave ? (
              <div
                className={
                  offerAttorneyNotify
                    ? 'matter-detail-page-save is-notifying'
                    : 'matter-detail-page-save'
                }
              >
                <button
                  type="submit"
                  form="demand-record-form"
                  className="btn-primary"
                  disabled={pending}
                >
                  {pending ? 'Saving…' : 'Save'}
                </button>
                {renderNotifyAttorneyCheckbox()}
              </div>
            ) : null}
          </div>

          <div className="debtor-detail-actions-mobile" ref={actionsRef}>
            <button
              type="button"
              className="debtor-detail-more-btn"
              aria-haspopup="menu"
              aria-expanded={actionsOpen}
              aria-label="More actions"
              onClick={() => setActionsOpen((open) => !open)}
            >
              <MoreActionsIcon />
            </button>
            {actionsOpen ? (
              <div className="debtor-detail-more-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="debtor-detail-more-item"
                  onClick={handleArchiveToggle}
                >
                  {isArchived ? 'Unarchive' : 'Archive'}
                </button>
                {canRecordPayment ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="debtor-detail-more-item"
                    disabled={pending}
                    onClick={focusPaymentForm}
                  >
                    Record Payment
                  </button>
                ) : null}
                {showTopTrackButton ? (
                  <a
                    role="menuitem"
                    className="debtor-detail-more-item"
                    href={mailTrackingUrl(form.Tracking_Number, trackingHints)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setActionsOpen(false)}
                  >
                    {trackPackageLabel}
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="matter-detail-stepper matter-detail-page-stepper">
        <StatusStepper status={record.Status} />
      </div>

      {statusBanner ? (
        <p className={`inline-notice matter-status-banner tone-${statusBanner.tone}`}>
          {statusBanner.message}
          {statusBanner.action ? ` ${statusBanner.action}` : ''}
        </p>
      ) : null}

      <MatterKpiStrip
        matter={kpiMatter}
        paymentTerms={paymentTerms}
        metricsOpen={metricsOpen}
        onToggleMetrics={setMetricsOpen}
      />

      <div className="record-tabs-wrap">
        <div
          className="record-tabs"
          role="tablist"
          aria-label="Demand record sections"
        >
          {MATTER_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            const hasNotify = Boolean(tabUnread[tab.id]);
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`matter-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`matter-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={selected ? 'record-tab active' : 'record-tab'}
                onClick={() => setTab(tab.id)}
              >
                <span className="record-tab-label">
                  <span className="record-tab-label-full">{tab.label}</span>
                  <span className="record-tab-label-short">{tab.shortLabel}</span>
                </span>
                {hasNotify ? (
                  <span
                    className="record-tab-notify"
                    aria-label={`New activity on ${tab.label}`}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="record-tab-panels">
      {activeTab === 'details' ? (
        <div className={previewOpen ? 'matter-record-split is-open' : 'matter-record-split'}>
          <section
            id="matter-panel-details"
            role="tabpanel"
            aria-labelledby="matter-tab-details"
            className="debtor-detail-panel"
          >
            <form id="demand-record-form" onSubmit={saveRecord}>
              <h3>Demand Details</h3>
              <p className="matter-record-meta">{recordMetaLine}</p>
              <div className="settings-grid matter-edit-grid">
                <div className="field field-span-2 matter-contact-summary">
                  <div className="matter-contact-summary-head">
                    <span>Debtor contact</span>
                  </div>
                  <div className="matter-contact-summary-body">
                    <strong>
                      {debtorDisplayName(form) || form.Debtor_Company || 'No contact name'}
                    </strong>
                    {form.Debtor_Company &&
                    form.Debtor_Company !== debtorDisplayName(form) ? (
                      <span>{form.Debtor_Company}</span>
                    ) : null}
                    {contactAddress ? <span>{contactAddress}</span> : null}
                    {form.Debtor_Email ? <span>{form.Debtor_Email}</span> : null}
                    <span className="matter-contact-summary-hint">
                      Edit company, address, and email on the debtor record to keep contact
                      details in one place.
                    </span>
                    {linkedDebtor ? (
                      <button
                        type="button"
                        className="text-link strong matter-contact-open-debtor"
                        onClick={() =>
                          openDebtor?.(
                            linkedDebtor,
                            `/client/demands/${encodeURIComponent(record.ROWID)}`
                          )
                        }
                      >
                        Open debtor record
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="field field-span-2">
                  <span>Dispute type</span>
                  {fieldsLocked ? (
                    <div className="field-value-stack">
                      <FieldValue tip={READ_ONLY_FIELD_TIP}>
                        {disputeLabel(form.Dispute_Type) || '—'}
                      </FieldValue>
                      {disputeTooltip(form.Dispute_Type) ? (
                        <span className="field-value-note">
                          {disputeTooltip(form.Dispute_Type)}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <DisputeTypeSelect
                      value={form.Dispute_Type}
                      onChange={(next) => updateField('Dispute_Type', next)}
                      required
                    />
                  )}
                </div>

                <div className="field">
                  <span className="field-label-with-tip">
                    Amount owed
                    <FieldTip
                      text={`${AMOUNT_OWED_TIP} Choose the currency for this balance (and future gateway charges). Separate from the debtor’s country.`}
                      label="Amount owed"
                    />
                  </span>
                  {fieldsLocked ? (
                    <FieldValue tip={READ_ONLY_FIELD_TIP}>
                      {formatAmountDisplay(form.Amount_Owed, form.Currency)}
                    </FieldValue>
                  ) : (
                    <div className="amount-currency-control">
                      <select
                        className="amount-currency-code"
                        aria-label="Currency"
                        value={normalizeMatterCurrency(form.Currency)}
                        onChange={(e) => updateField('Currency', e.target.value)}
                        required
                      >
                        {MATTER_CURRENCIES.map((item) => (
                          <option key={item.value} value={item.value} title={item.label}>
                            {item.value}
                          </option>
                        ))}
                      </select>
                      <input
                        className="amount-currency-value"
                        required
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        aria-label="Amount owed"
                        value={formatCurrencyInputDisplay(form.Amount_Owed, form.Currency, {
                          withSymbol: false,
                        })}
                        onChange={(e) =>
                          updateField('Amount_Owed', parseCurrencyInputValue(e.target.value))
                        }
                        onBlur={() => {
                          const committed = commitCurrencyAmount(form.Amount_Owed);
                          if (!committed) return;
                          updateField('Amount_Owed', committed);
                        }}
                        placeholder={`${currencyDisplaySymbol(form.Currency)}0.00`}
                      />
                    </div>
                  )}
                </div>
                <div className="field">
                  <span>Invoice ID</span>
                  {fieldsLocked ? (
                    <FieldValue tip={READ_ONLY_FIELD_TIP}>
                      {form.External_Invoice_ID || '—'}
                    </FieldValue>
                  ) : (
                    <input
                      type="text"
                      value={form.External_Invoice_ID}
                      onChange={(e) => updateField('External_Invoice_ID', e.target.value)}
                      placeholder="If known"
                    />
                  )}
                </div>
                <div className="field">
                  <span className="field-label-with-tip">
                    Account #
                    <FieldTip
                      text="Your ID for this customer in your system (not the invoice number)."
                      label="Account #"
                    />
                  </span>
                  {fieldsLocked ? (
                    <FieldValue tip={READ_ONLY_FIELD_TIP}>
                      {form.External_Account_Number || '—'}
                    </FieldValue>
                  ) : (
                    <input
                      type="text"
                      value={form.External_Account_Number}
                      onChange={(e) => updateField('External_Account_Number', e.target.value)}
                      placeholder="If known"
                    />
                  )}
                </div>
                {String(form.Due_Date || record?.Due_Date || '').trim() ? (
                  <div className="field">
                    <span className="field-label-with-tip">
                      Due date
                      <FieldTip
                        text="If this date passes with no payment recorded (partial or full), the demand moves to Unpaid automatically."
                        label="Due date"
                      />
                    </span>
                    <FieldValue tip={DUE_DATE_FIELD_TIP}>
                      {form.Due_Date || record?.Due_Date || '—'}
                    </FieldValue>
                  </div>
                ) : null}
                <div className="matter-choice-row field-span-2">
                  <div className="field">
                    <span className="field-label-with-tip">
                      Will you accept a payment plan?
                      <FieldTip text={PAYMENT_PLAN_HINT} label="Payment plan" />
                    </span>
                    {fieldsLocked ? (
                      <FieldValue tip={READ_ONLY_FIELD_TIP}>
                        {selectedPlan?.label || '—'}
                      </FieldValue>
                    ) : (
                      <select
                        value={form.installmentSelection}
                        onChange={(e) => updateField('installmentSelection', e.target.value)}
                      >
                        {planOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="field">
                    <span className="field-label-with-tip">
                      Will you consider a settlement offer?
                      <FieldTip text={SETTLEMENT_CHOICE_HINT} label="Settlement" />
                    </span>
                    {fieldsLocked ? (
                      <FieldValue tip={READ_ONLY_FIELD_TIP}>
                        {settlementChoiceLabel(form.settlementChoice)}
                      </FieldValue>
                    ) : (
                      <select
                        value={form.settlementChoice}
                        onChange={(e) => updateField('settlementChoice', e.target.value)}
                      >
                        {SETTLEMENT_CHOICES.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <EvidenceUpload
                  fileNames={form.evidenceFileNames}
                  files={evidenceFiles}
                  onFilesSelected={onEvidenceFiles}
                  onRemoveFile={removeEvidenceFile}
                  onRemoveAttachment={removeMatterAttachment}
                  attachments={attachments}
                  ownerId={record.ROWID}
                  vaultUrl={form.Evidence_Vault_URL || record.Evidence_Vault_URL || ''}
                  onPreviewFile={openEvidencePreview}
                  previewName={filePreview?.name || ''}
                  onViewerUrls={syncEvidenceViewerUrls}
                  disabled={!canEditEvidence}
                  allowRemove={canRemoveEvidence}
                  storageBlocked={canEditEvidence && Boolean(vaultUsage?.atLimit)}
                  storageMessage={
                    canEditEvidence && vaultUsage?.atLimit
                      ? evidenceVaultLimitMessage(vaultUsage)
                      : ''
                  }
                  storageWarn={
                    canEditEvidence && Boolean(vaultUsage?.atWarn && !vaultUsage?.atLimit)
                  }
                  storageWarnMessage={
                    canEditEvidence && vaultUsage?.atWarn && !vaultUsage?.atLimit
                      ? evidenceVaultWarnMessage(vaultUsage)
                      : ''
                  }
                />

                {showAssignedExtras ? (
                  <>
                    <div className="field">
                      <span>Tracking number</span>
                      <div className={canTrackPackage ? 'tracking-number-row' : undefined}>
                        <FieldValue tip={TRACKING_FIELD_TIP}>
                          {form.Tracking_Number || '—'}
                        </FieldValue>
                        {renderTrackPackageButton('btn-primary matter-detail-track-btn')}
                      </div>
                    </div>
                    <div className="field">
                      <span>Assigned attorney</span>
                      <FieldValue tip={ATTORNEY_FIELD_TIP}>
                        {attorney?.Display_Name ||
                          record.Attorney_Name ||
                          'Not assigned yet'}
                      </FieldValue>
                    </div>
                  </>
                ) : null}
              </div>

              {error ? <p className="form-error">{error}</p> : null}

              {showSave ? (
                <div
                  className={
                    offerAttorneyNotify
                      ? 'drawer-actions matter-detail-form-save is-notifying'
                      : 'drawer-actions matter-detail-form-save'
                  }
                >
                  <button type="submit" className="btn-primary" disabled={pending}>
                    {pending ? 'Saving…' : 'Save'}
                  </button>
                  {renderNotifyAttorneyCheckbox()}
                </div>
              ) : null}
            </form>

            {showAssignedExtras ? (
              <div className="matter-detail-draft-block matter-demand-letter-block">
                <div className="matter-demand-letter-head">
                  <h3>Demand letter</h3>
                  {letterViewerUrl ? (
                    <a
                      className="text-link strong"
                      href={letterViewerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open PDF
                    </a>
                  ) : null}
                </div>
                {letterViewerUrl ? (
                  <div className="demand-letter-viewer">
                    <iframe
                      className="demand-letter-pdf"
                      title={`Demand letter PDF — ${letterFileName}`}
                      src={letterViewerUrl}
                    />
                  </div>
                ) : (
                  <p className="empty-inline">
                    The demand letter PDF will appear here once your attorney partner uploads
                    it.
                  </p>
                )}
              </div>
            ) : null}
          </section>
          {previewOpen ? (
            <DemandFilePreview file={filePreview} onClose={() => setFilePreview(null)} />
          ) : null}
        </div>
        ) : null}

        {activeTab === 'payments' ? (
          <section
            id="matter-panel-payments"
            role="tabpanel"
            aria-labelledby="matter-tab-payments"
            className="debtor-detail-panel"
          >
            <div className="debtor-detail-panel-head matter-detail-payment-actions">
              <div>
                <h3>Payments</h3>
                <p className="settings-sublead">
                  Record full or partial payments accepted outside the portal — whether or not
                  you accept a payment plan — plus collections your attorney partner reports.
                </p>
              </div>
            </div>

            {error ? <p className="form-error">{error}</p> : null}

            {canRecordPayment ? (
              <form
                ref={paymentFormRef}
                className="matter-payment-form"
                onSubmit={recordDebtorPayment}
              >
                <label className="field">
                  <span>Payment amount</span>
                  <input
                    name="payment-amount"
                    type="number"
                    min="0.01"
                    step="0.01"
                    inputMode="decimal"
                    required
                    value={paymentAmount}
                    onChange={(e) => {
                      setPaymentAmount(sanitizeCurrencyInput(e.target.value));
                      setPaymentError('');
                    }}
                    placeholder={
                      balanceRemaining > 0
                        ? `Up to ${formatAmountDisplay(
                            balanceRemaining,
                            form.Currency || record?.Currency
                          )}`
                        : '0.00'
                    }
                  />
                </label>
                <label className="field">
                  <span>Reference #</span>
                  <input
                    name="payment-reference"
                    type="text"
                    value={paymentReference}
                    onChange={(e) => {
                      setPaymentReference(e.target.value);
                      setPaymentError('');
                    }}
                    placeholder="Check, ACH, wire, or remittance #"
                    autoComplete="off"
                  />
                </label>
                <div className="field matter-payment-proof">
                  <span>Proof of payment</span>
                  <PaymentProofUploader
                    files={paymentProofFiles}
                    disabled={pending}
                    onFilesSelected={(accepted) =>
                      onPaymentProofFilesChange(accepted, { mode: 'add' })
                    }
                    onRemoveFile={(name) =>
                      onPaymentProofFilesChange([name], { mode: 'remove' })
                    }
                  />
                </div>
                {paymentError ? <p className="form-error">{paymentError}</p> : null}
                <div className="drawer-actions">
                  <button type="submit" className="btn-primary" disabled={pending}>
                    {pending ? 'Recording…' : 'Record Payment'}
                  </button>
                </div>
              </form>
            ) : null}

            {paymentEntries.length ? (
              <ul className="debtor-related-demands matter-payment-entries">
                {[...paymentEntries].reverse().map((entry) => (
                  <li key={entry.id}>
                    <div className="debtor-related-demand is-static matter-payment-entry">
                      <span className="debtor-related-demand-top">
                        <strong>
                          {formatAmountDisplay(
                            entry.amount,
                            entry.currency || form.Currency || record?.Currency
                          )}
                        </strong>
                        <span className="status-pill">Payment</span>
                        <button
                          type="button"
                          className="evidence-file-remove matter-payment-delete"
                          aria-label={`Delete payment of ${formatAmountDisplay(
                            entry.amount,
                            entry.currency || form.Currency || record?.Currency
                          )}`}
                          title="Delete payment"
                          disabled={pending}
                          onClick={() => deletePaymentEntry(entry)}
                        >
                          <TrashIcon size={16} />
                        </button>
                      </span>
                      <span className="debtor-related-demand-meta">
                        Recorded {formatWhen(entry.paidAt) || '—'}
                        {entry.reference ? ` · Ref ${entry.reference}` : ''}
                        {entry.note ? ` · ${entry.note}` : ''}
                      </span>
                      {entry.attachments?.length ? (
                        <PaymentProofAttachments
                          matterId={record.ROWID}
                          paymentId={entry.id}
                          names={entry.attachments}
                        />
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : isPaidStatus(record.Status) ? (
              <ul className="debtor-related-demands matter-payment-entries">
                <li>
                  <div className="debtor-related-demand is-static">
                    <span className="debtor-related-demand-top">
                      <strong>
                        {formatAmountDisplay(
                          form.Amount_Owed || record.Amount_Owed,
                          form.Currency || record?.Currency
                        )}
                      </strong>
                      <span className={`status-pill status-${record.Status}`}>
                        {statusLabel}
                      </span>
                    </span>
                    <span className="debtor-related-demand-meta">
                      {paidBeforeAssignment
                        ? 'Paid before assignment'
                        : 'Payment recorded on this demand'}
                      {record.Paid_At ? ` · ${formatWhen(record.Paid_At)}` : ''}
                    </span>
                  </div>
                </li>
              </ul>
            ) : !canRecordPayment ? (
              <p className="empty-inline">
                No payments recorded on this demand yet.
              </p>
            ) : null}
          </section>
        ) : null}

        {activeTab === 'messages' ? (
          <section
            id="matter-panel-messages"
            role="tabpanel"
            aria-labelledby="matter-tab-messages"
            className="debtor-detail-panel"
          >
            <h3>Attorney communication</h3>
            <p className="settings-sublead">
              Messages between you and your attorney on this demand. All demand conversations
              also appear under{' '}
              <Link
                className="text-link strong"
                to={`/client/messages?demand=${encodeURIComponent(record.ROWID)}`}
              >
                Messages
              </Link>
              .
            </p>

            <div className="message-thread" ref={threadRef}>
              {messages.length ? (
                messages.map((msg) => (
                  <MessageBubble
                    key={msg.ROWID}
                    message={msg}
                    canDelete
                    deleting={deletingMessageId === msg.ROWID}
                    deleteDisabled={Boolean(deletingMessageId) || pending}
                    onDelete={deleteAttorneyMessage}
                  />
                ))
              ) : (
                <p className="empty-inline">No messages yet. Start the conversation below.</p>
              )}
            </div>

            <MessageComposer
              draft={draft}
              onDraftChange={setDraft}
              attachmentFiles={attachmentFiles}
              onAttachmentFilesChange={onMessageAttachmentFilesChange}
              onSubmit={sendMessage}
              pending={pending}
              error={error}
              placeholder={messageComposerPlaceholder(record || form)}
            />
          </section>
        ) : null}

        {activeTab === 'history' ? (
          <section
            id="matter-panel-history"
            role="tabpanel"
            aria-labelledby="matter-tab-history"
            className="debtor-detail-panel"
          >
            <h3>History</h3>
            <p className="settings-sublead">
              Activity on this demand record — created, updates, evidence, and key events.
            </p>
            <ol className="matter-history-timeline debtor-history-timeline" ref={historyRef}>
              {history.length ? (
                [...history].reverse().map((event) => (
                  <li key={event.ROWID} className="matter-history-item">
                    <div className="matter-history-marker" aria-hidden="true" />
                    <div className="matter-history-body">
                      <div className="matter-history-meta">
                        <span className="matter-history-type">
                          {historyTypeLabel(event.Event_Type)}
                        </span>
                        <time dateTime={event.Created_At || undefined}>
                          {formatWhen(event.Created_At)}
                        </time>
                      </div>
                      <p className="matter-history-summary">{event.Summary}</p>
                      {event.Actor_Name ? (
                        <p className="matter-history-actor">{event.Actor_Name}</p>
                      ) : null}
                    </div>
                  </li>
                ))
              ) : (
                <li className="matter-history-empty">
                  <p className="empty-inline">No history yet for this demand.</p>
                </li>
              )}
            </ol>
          </section>
        ) : null}

        {activeTab === 'notes' ? (
          <section
            id="matter-panel-notes"
            role="tabpanel"
            aria-labelledby="matter-tab-notes"
            className="debtor-detail-panel"
          >
            <RecordNotesSection
              kind="matter"
              recordId={record.ROWID}
              user={user}
              hideTitle
              unframed
              onEntriesChange={() => setNotesRevision((n) => n + 1)}
              lead="Private comments for your team on this demand. Each note is saved with your name and a timestamp — including after the demand is sent."
              placeholder="Add a note — call outcomes, payment promises, internal context…"
            />
          </section>
        ) : null}
      </div>

      {showSave ? (
        <div
          className={
            offerAttorneyNotify
              ? 'debtor-detail-mobile-save is-notifying'
              : 'debtor-detail-mobile-save'
          }
        >
          <button
            type="submit"
            form="demand-record-form"
            className="btn-primary"
            disabled={pending}
          >
            {pending ? 'Saving…' : 'Save'}
          </button>
          {renderNotifyAttorneyCheckbox()}
        </div>
      ) : null}
    </div>
  );
}
